'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { BaseButton } from '@7f-ui/components/button/button';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Modal } from '@7f-ui/components/modal/modal';
import { Price } from '@7f-ui/components/price';
import { useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { useExperienceDuration } from '@feat/feat-experience/data';
import { getTodayInDefaultTimeZone, parseLocalDate, parseLocalDay } from '@utils/helpers';
import { formatDate, formatIsoDate } from '@utils/utils-format';
import { DotPulse } from 'ldrs/react';
import 'ldrs/react/DotPulse.css';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useAppExperienceDayPrices } from '../_hooks/use-app-experience-day-prices';
import { useExperienceOptions } from '../_hooks/use-app-experience-options';
import { useAppExperiencePersonsLabel } from '../_hooks/use-app-experience-persons-label';
import { useEdpTimeslotsForDate } from '../_hooks/use-edp-timeslots-for-date';
import { type EdpTimeslot, mapDatesToEdpTimeslots } from '../_lib/experience-booking-utils';
import { monthAround, useExperienceFilterContext } from './app-experience-filter-provider';
const DatePicker = dynamic(() => import('@7f-ui/components/datepicker/date-picker/date-picker').then(module => module.DatePicker), {
  ssr: false,
  loading: () => <div className="grid h-full place-content-center sm:min-h-96">
        <DotPulse speed={1.3} color="currentColor" />
      </div>
});
type SubmitCallbacks = {
  onSuccess: () => void;
  onError: () => void;
};
type AppEdpBookingFlowProps = {
  availableDates?: string[];
  availableDatesArePreliminary?: boolean;
  initialDate?: Date | null;
  initialTimeslotId?: string;
  submitLabel: string;
  mode?: 'add' | 'update';
  onSubmit: (timeslot: EdpTimeslot, callbacks: SubmitCallbacks) => void;
  extraBottomContent?: React.ReactNode;
};
export function AppEdpBookingFlow(t0) {
  const $ = _c(44);
  const {
    availableDates,
    availableDatesArePreliminary: t1,
    initialDate: t2,
    initialTimeslotId,
    submitLabel,
    mode: t3,
    onSubmit,
    extraBottomContent
  } = t0;
  const availableDatesArePreliminary = t1 === undefined ? false : t1;
  const initialDate = t2 === undefined ? null : t2;
  const mode = t3 === undefined ? "add" : t3;
  const {
    filter,
    experience,
    baseVariants
  } = useExperienceFilterContext();
  const isCurrencyMismatch = useIsCartCurrencyMismatch(mode === "update" ? undefined : experience.currency);
  const {
    allOptions
  } = useExperienceOptions(baseVariants);
  let t4;
  if ($[0] !== allOptions || $[1] !== filter.selectedOptions) {
    t4 = {
      allOptions,
      selectedOptions: filter.selectedOptions,
      fallback: ""
    };
    $[0] = allOptions;
    $[1] = filter.selectedOptions;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const paxLabel = useAppExperiencePersonsLabel(t4);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedTimeslot, setSelectedTimeslot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAppliedInitialTimeslot, setHasAppliedInitialTimeslot] = useState(false);
  const [lockedTimeslotHeight, setLockedTimeslotHeight] = useState(0);
  const timeslotContentRef = useRef(null);
  const [appliedDates, setAppliedDates] = useState(undefined);
  let t5;
  if ($[3] !== availableDates) {
    t5 = availableDates ?? [];
    $[3] = availableDates;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = new Set(t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const availableDateSet = t6;
  let t7;
  if ($[7] !== availableDateSet) {
    t7 = cell => {
      const dateStr = formatIsoDate(cell.date);
      const isAvailable = availableDateSet.has(dateStr);
      return <div className="relative flex size-11 items-center justify-center"><span>{cell.formattedDate}</span>{isAvailable && <span className="absolute bottom-2.5 size-[4px] rounded-full bg-success" />}</div>;
    };
    $[7] = availableDateSet;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const renderDateWithDot = t7;
  const {
    dates: timeslotDates,
    isPreliminary: timeslotsArePreliminary
  } = useEdpTimeslotsForDate(selectedDate);
  let t8;
  if ($[9] !== filter.selectedOptions) {
    t8 = Object.keys(filter.selectedOptions ?? {});
    $[9] = filter.selectedOptions;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const selectedOptions = t8;
  let t9;
  if ($[11] !== selectedOptions) {
    t9 = option => selectedOptions.includes(option.id);
    $[11] = selectedOptions;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const options = allOptions.filter(t9);
  let t10;
  if ($[13] !== selectedDate) {
    t10 = selectedDate ? monthAround(formatIsoDate(selectedDate)) : null;
    $[13] = selectedDate;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const selectedMonth = t10;
  const {
    dayPrices,
    arePreliminary: dayPricesArePreliminary,
    isError: dayPricesFailed
  } = useAppExperienceDayPrices(experience.id, selectedMonth?.from, selectedMonth?.to);
  const timeslots = mapDatesToEdpTimeslots(timeslotDates, options, filter.selectedOptions, dayPrices);
  if (!hasAppliedInitialTimeslot && initialTimeslotId && timeslots.length > 0) {
    let t11;
    if ($[15] !== initialTimeslotId) {
      t11 = ts => ts.id === initialTimeslotId;
      $[15] = initialTimeslotId;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    const match = timeslots.find(t11);
    if (match) {
      setSelectedTimeslot(match);
    }
    setHasAppliedInitialTimeslot(true);
  }
  if (!timeslotsArePreliminary && selectedTimeslot && !timeslots.some(timeslot => timeslot.id === selectedTimeslot.id)) {
    setSelectedTimeslot(null);
  }
  let t11;
  if ($[17] !== selectedDate) {
    t11 = () => {
      if (!selectedDate) {
        return;
      }
      const measured = timeslotContentRef.current?.offsetHeight ?? 0;
      setLockedTimeslotHeight(prev => Math.max(prev, measured));
    };
    $[17] = selectedDate;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  useEffect(t11, [selectedDate, timeslots]);
  let maxDate;
  let minDate;
  let t12;
  if ($[19] !== availableDates || $[20] !== experience.offerPeriodEnd || $[21] !== experience.offerPeriodStart) {
    const today = getTodayInDefaultTimeZone();
    minDate = experience.offerPeriodStart ? parseLocalDate(experience.offerPeriodStart) : today;
    maxDate = experience.offerPeriodEnd?.local ? parseLocalDate(experience.offerPeriodEnd) : today;
    t12 = firstAvailableDate(availableDates, minDate, maxDate);
    $[19] = availableDates;
    $[20] = experience.offerPeriodEnd;
    $[21] = experience.offerPeriodStart;
    $[22] = maxDate;
    $[23] = minDate;
    $[24] = t12;
  } else {
    maxDate = $[22];
    minDate = $[23];
    t12 = $[24];
  }
  const preselectableDate = t12;
  if (!availableDatesArePreliminary && appliedDates !== availableDates) {
    setAppliedDates(availableDates);
    if (!initialDate) {
      const bookable = new Set(availableDates ?? []);
      if (!selectedDate || !bookable.has(formatIsoDate(selectedDate))) {
        setSelectedDate(preselectableDate);
        setSelectedTimeslot(null);
      }
    }
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = date => {
      setSelectedDate(date);
      setSelectedTimeslot(null);
      if (!date) {
        setLockedTimeslotHeight(0);
      }
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleDateChange = t13;
  const handleSubmit = () => {
    if (!selectedTimeslot) {
      return;
    }
    const bookable_0 = timeslots.find(timeslot_0 => timeslot_0.id === selectedTimeslot.id);
    if (!bookable_0 || timeslotsArePreliminary) {
      return;
    }
    if (dayPricesArePreliminary || dayPricesFailed) {
      return;
    }
    setIsSubmitting(true);
    onSubmit(bookable_0, {
      onSuccess: () => setIsSubmitting(false),
      onError: () => setIsSubmitting(false)
    });
  };
  const t14 = Modal;
  const t15 = "px-0 py-4";
  const t16 = () => <Modal.Footer><Modal.Footer.Actions>{selectedDate && <Modal.Footer.Button variant="secondary" onClick={() => handleDateChange(null)}>Zurücksetzen</Modal.Footer.Button>}<Modal.Footer.Button disabled={!selectedTimeslot || timeslotsArePreliminary || dayPricesArePreliminary || dayPricesFailed || isSubmitting || isCurrencyMismatch} onClick={handleSubmit}>{submitLabel}</Modal.Footer.Button></Modal.Footer.Actions></Modal.Footer>;
  const t17 = "flex flex-col gap-4 overflow-y-auto";
  let t18;
  if ($[26] !== maxDate || $[27] !== minDate || $[28] !== renderDateWithDot || $[29] !== selectedDate) {
    t18 = <DatePicker value={selectedDate} min={minDate} max={maxDate} onChange={handleDateChange} renderDate={renderDateWithDot} />;
    $[26] = maxDate;
    $[27] = minDate;
    $[28] = renderDateWithDot;
    $[29] = selectedDate;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const t19 = "px-4";
  let t20;
  if ($[31] !== lockedTimeslotHeight) {
    t20 = lockedTimeslotHeight > 0 ? {
      minHeight: lockedTimeslotHeight
    } : undefined;
    $[31] = lockedTimeslotHeight;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const t21 = selectedDate && <div ref={timeslotContentRef} className="flex flex-col gap-3"><p className="font-body-copy font-semibold">{formatDate(selectedDate)}</p>{timeslots.length === 0 ? <MessageBox type="error">Keine freien Termine an diesem Tag.</MessageBox> : <div className="flex flex-col gap-2">{timeslots.map(timeslot_1 => <TimeslotButton key={timeslot_1.id} timeslot={timeslot_1} paxLabel={paxLabel} currency={experience.currency} isSelected={selectedTimeslot?.id === timeslot_1.id} onClick={() => setSelectedTimeslot(prev_0 => prev_0?.id === timeslot_1.id ? null : timeslot_1)} />)}</div>}</div>;
  let t22;
  if ($[33] !== t20 || $[34] !== t21) {
    t22 = <div className={t19} style={t20}>{t21}</div>;
    $[33] = t20;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== extraBottomContent || $[37] !== t18 || $[38] !== t22) {
    t23 = <div className={t17}>{t18}{t22}{extraBottomContent}</div>;
    $[36] = extraBottomContent;
    $[37] = t18;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t14.Content || $[41] !== t16 || $[42] !== t23) {
    t24 = <t14.Content className={t15} renderFooter={t16}>{t23}</t14.Content>;
    $[40] = t14.Content;
    $[41] = t16;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  return t24;
}
type TimeslotButtonProps = {
  timeslot: EdpTimeslot;
  paxLabel: React.ReactNode;
  currency: string;
  isSelected: boolean;
  onClick: () => void;
};
function TimeslotButton(t0) {
  const $ = _c(21);
  const {
    timeslot,
    paxLabel,
    currency,
    isSelected,
    onClick
  } = t0;
  const duration = useExperienceDuration(timeslot.duration);
  const t1 = isSelected ? "border-primary bg-primary/5" : "border-primary-green/15 bg-white hover:bg-hover-light active:bg-active-light";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("flex items-center gap-3 rounded-xl border p-4 text-left transition-colors", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `${timeslot.startTime} Uhr`;
  let t4;
  if ($[2] !== t3) {
    t4 = <span className="whitespace-nowrap font-body-copy font-semibold">{t3}</span>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span aria-hidden={true} className="h-8 w-px shrink-0 bg-primary-green/15" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== paxLabel) {
    t6 = <span>{paxLabel}</span>;
    $[5] = paxLabel;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== duration) {
    t7 = <span>{duration}</span>;
    $[7] = duration;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <span className="flex min-w-0 flex-1 flex-col font-body-small text-secondary-text">{t6}{t7}</span>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== currency || $[13] !== timeslot.price) {
    t9 = <Price amount={timeslot.price} currency={currency} className="whitespace-nowrap font-body-copy font-semibold" />;
    $[12] = currency;
    $[13] = timeslot.price;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== onClick || $[16] !== t2 || $[17] !== t4 || $[18] !== t8 || $[19] !== t9) {
    t10 = <BaseButton onClick={onClick} className={t2}>{t4}{t5}{t8}{t9}</BaseButton>;
    $[15] = onClick;
    $[16] = t2;
    $[17] = t4;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
export function firstAvailableDate(availableDates: string[] | undefined, minDate?: Date, maxDate?: Date): Date | null {
  const min = minDate ? formatIsoDate(minDate) : undefined;
  const max = maxDate ? formatIsoDate(maxDate) : undefined;
  const earliest = [...(availableDates ?? [])].filter(day => (!min || day >= min) && (!max || day <= max)).toSorted()[0];
  if (!earliest) return null;
  return parseLocalDay(earliest);
}